import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Search, Download } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminNewsletter() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/newsletter");
      setItems(res.data || []);
    } catch { toast.error("Failed to load subscribers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => (it.email || "").toLowerCase().includes(q));
  }, [items, query]);

  const del = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try { await api.delete(`/admin/newsletter/${id}`); toast.success("Removed"); await load(); }
    catch { toast.error("Failed to remove"); }
  };

  const exportCsv = () => {
    const rows = [["email", "subscribed_at"], ...filtered.map(it => [it.email, it.created_at])];
    const csv = rows.map(r => r.map(c => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `homecure_newsletter_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-newsletter">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#0F2A4A]">Newsletter Subscribers</h1>
          <p className="text-slate-600 text-sm">Email subscribers from the website.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search email..." className="hc-input pl-9 w-64" />
          </div>
          <button onClick={exportCsv} className="hc-btn-secondary"><Download className="w-4 h-4" /> Export CSV</button>
        </div>
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#3780E0]" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No subscribers yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#3780E0] text-white text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Subscribed</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it, idx) => (
                  <tr key={it.id} className={idx % 2 ? "bg-[#E8F0FB]" : "bg-white"}>
                    <td className="px-4 py-3 font-semibold text-[#0F2A4A]">{it.email}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(it.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => del(it.id)} className="p-2 rounded hover:bg-red-50" title="Remove"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
